import { setDay } from "date-fns";
import { useTranslation } from "react-i18next";
import Button from "../../components/UI/Button/Button";
import "./Weekpicker.scss";
import WeekInput from "../../components/UI/WeekInput/WeekInput";
import { useEffect } from "react";
import { getMondayOfWeek } from "../../shared/utility";

type Props = {
  value: Date | null;
  onChange: (value: Date) => void;
  includeWeekdays: boolean;
  textM?: boolean;
  isSmall?: boolean;
  onWeekChange?: (date: Date) => void;
  /**
   * The exact day that was clicked, before it is rounded to its Monday.
   *
   * `onChange` has to keep returning the Monday because callers use its value
   * as the start of the week, so the picked day would otherwise be lost.
   */
  onDateSelect?: (date: Date) => void;
};

function WeekSelector(props: Props) {
  const { t } = useTranslation();
  const weekDay = props.value ? props.value.getDay() : null;
  const weekDays = [
    { name: "M", value: 1 },
    { name: "T", value: 2 },
    { name: "O", value: 3 },
    { name: "T", value: 4 },
    { name: "F", value: 5 },
    { name: "L", value: 6 },
    { name: "S", value: 0 },
  ];

  const adjustToSwedishTime = (date: Date): Date => {
    const now = new Date();
    const selectedDate = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );
    return new Date(
      selectedDate.toLocaleString("sv-SE", { timeZone: "Europe/Stockholm" })
    );
  };

  useEffect(() => {
    // Only seed a week when the caller has none. This component is often
    // rendered conditionally — the rehab tab on /bokningar is one — so it
    // remounts on every switch, and seeding unconditionally threw away the
    // week the user had chosen and jumped back to the current one.
    if (props.value) return;

    const monday = getMondayOfWeek(new Date());
    props.onChange(monday);
    if (props.onWeekChange) {
      props.onWeekChange(monday);
    }
  }, []);

  const textM = props.textM ? "text-m" : "";

  return (
    <div className={`week-selector ${textM}`}>
      <WeekInput
        value={props.value ? getMondayOfWeek(props.value) : null}
        onChange={(value) => {
          if (value) {
            props.onChange(getMondayOfWeek(value));
            // Picking Thursday the 27th should land on the 27th, not snap the
            // view back to that Monday.
            props.onDateSelect?.(value);
          }
        }}
        width="135px"
        showAllDates
        ignoreUnselectableDates
        showWeekNumbers
        isSmall={props.isSmall}
      />
      <i className="fa-regular fa-calendar absolute-calendar"></i>
      {props.includeWeekdays ? (
        weekDays.map((day) => (
          <Button
            key={day.value}
            label={t(`${day.name}`)}
            color={weekDay === day.value ? "secondary" : "primary"}
            onClick={() => {
              const selectedDate = setDay(
                props.value || new Date(),
                day.value,
                { weekStartsOn: 1 }
              );
              const swedishDate = adjustToSwedishTime(selectedDate);
              props.onChange(getMondayOfWeek(swedishDate));
              props.onDateSelect?.(swedishDate);
            }}
            short
          />
        ))
      ) : (
        <></>
      )}
    </div>
  );
}

export default WeekSelector;
